import { useCallback, useEffect, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { BookOpen } from "lucide-react";
import { api } from "@/lib/api";
import { Logo } from "@/components/Brand";
import { Sheet } from "@/components/Sheet";
import { SideContent, NavTabs } from "@/components/SideContent";
import { RemansoMargin } from "@/components/RemansoMargin";
import { MobileRemansoBar } from "@/components/MobileRemansoBar";
import { RemansoProvider, useRemanso } from "@/context/RemansoContext";
import { useAuth } from "@/context/AuthContext";
import { InfoPanel } from "@/components/InfoPanel";
import { UpdateNotice } from "@/components/UpdateNotice";
import { syncDrafts } from "@/lib/drafts";

const WelcomeOnce = () => {
  const { user } = useAuth();
  const { openInfo } = useRemanso();
  const pending = !!user?.welcome_pending;
  useEffect(() => { if (pending) openInfo("bienvenida"); }, [pending, openInfo]);
  return null;
};

const MobileHeader = ({ onOpenList }) => (
  <header className="lg:hidden sticky top-0 z-30 bg-papel border-b border-linea px-4 pt-[calc(0.75rem+env(safe-area-inset-top))] pb-2">
    <div className="flex items-center justify-between gap-3">
      <Logo size={52} testId="mobile-logo" />
      <button className="btn-secondary px-4 py-2" onClick={onOpenList} data-testid="open-entries-button">
        <BookOpen size={16} strokeWidth={1.75} /> Mis entradas
      </button>
    </div>
    <NavTabs prefix="mobile" className="mt-3" />
  </header>
);

export default function AppShell() {
  const uid = useAuth().user?.user_id;
  const [entries, setEntries] = useState([]);
  const [error, setError] = useState("");
  const [listOpen, setListOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [draftsWaiting, setDraftsWaiting] = useState(false);

  // unsent drafts from a previous session go to the server before the entries load
  useEffect(() => {
    let alive = true;
    const load = () => api.get("/entries")
      .then((r) => { setEntries(r.data); setError(""); })
      .catch(() => setError("No pudimos traer tus entradas en este momento. Intenta de nuevo en un rato."))
      .finally(() => setLoaded(true));
    let waiting = false;
    const retry = async () => {
      if (!waiting || !(await syncDrafts(uid)) || !alive) return;
      waiting = false;
      setDraftsWaiting(false);
      load();
    };
    syncDrafts(uid).catch(() => false).then((ok) => {
      if (!alive) return;
      waiting = !ok;
      setDraftsWaiting(!ok);
      load();
    });
    window.addEventListener("online", retry);
    return () => { alive = false; window.removeEventListener("online", retry); };
  }, [uid]);

  const upsertEntry = useCallback((entry) => setEntries((list) =>
    [entry, ...list.filter((e) => e.id !== entry.id)].sort((a, b) => b.created_at.localeCompare(a.created_at))), []);
  const removeEntry = useCallback((id) => setEntries((list) => list.filter((e) => e.id !== id)), []);
  const patchEntry = useCallback((id, patch) => setEntries((list) => list.map((e) => (e.id === id ? { ...e, ...patch } : e))), []);

  const path = useLocation().pathname;
  const onHabits = ["/habitos", "/mi-cuenta", "/privacidad", "/terminos", "/instalar"].some((p) => path.startsWith(p));

  return (
    <RemansoProvider>
      <div className={`min-h-screen bg-papel lg:grid ${onHabits ? "lg:grid-cols-[248px_minmax(0,1fr)]" : "lg:grid-cols-[248px_minmax(0,1fr)_340px]"} lg:gap-6 lg:p-8 lg:items-start`} data-testid="app-shell">
        <aside className="hidden lg:flex flex-col h-[calc(100vh-4rem)] sticky top-8 gap-6" data-testid="left-column">
          <Logo size={52} testId="desktop-logo" />
          <SideContent entries={entries} error={error} prefix="desktop" />
        </aside>
        <MobileHeader onOpenList={() => setListOpen(true)} />
        <main className="min-w-0 px-4 pt-5 pb-[calc(7rem+env(safe-area-inset-bottom))] lg:p-0" data-testid="center-column">
          {draftsWaiting && <p className="calm-note mb-4" role="status" data-testid="drafts-waiting-note">Tienes texto guardado en este dispositivo. Lo verás en cuanto vuelva la conexión.</p>}
          <Outlet context={{ entries, loaded, upsertEntry, removeEntry, patchEntry }} />
        </main>
        {!onHabits && <RemansoMargin />}
      </div>
      <MobileRemansoBar helpOnly={onHabits} />
      <InfoPanel />
      <UpdateNotice />
      <WelcomeOnce />
      <Sheet side="left" open={listOpen} onOpenChange={setListOpen} title="Mis entradas" testId="entries-sheet">
        <SideContent entries={entries} error={error} prefix="sheet" showTabs={false} onNavigate={() => setListOpen(false)} />
      </Sheet>
    </RemansoProvider>
  );
}
